import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import i18n from "../i18n";

/**
 * Onay metinleri (KVKK, giriş kuralları, denetim kuralları, taahhütnameler) — backlog #536, 2026-10-21.
 * Sözleşme: docs/guides/FE-OnayMetniController-guide.md. Canlı, mock yok.
 *
 * Bir metnin ANAHTARI (onayMetniKey) bir sayfanın metni istediği addır; sayfa anahtarı bir kez sorar, kullanıcının
 * dilinde alır (EN yoksa TR). Metin sürüm sürüm tutulur: YAYINLANMIŞ sürüm donar (PUT 409 döner), değişiklik yeni bir
 * taslaktır ve yayınlamak herkesin metni yeniden onaylamasını ister. Yayından geri alma yoktur.
 *
 * Yönetim okumaları (liste, sürümler, taslak metinleri) yalnızca personel; yazmalar `onay_metni.manage` ister
 * (PERMISSION_KEYS.ONAY_METNI_MANAGE, #253). Yetkisizde 403 ve mesajı yetkiyi adıyla söyler.
 */
const BASE = `${JAVA_BACKEND_API_URL}/onay-metinleri`;

/** language_id: 1 Türkçe, 2 İngilizce (LanguageConstant). Başkası yazmada 400. */
export const DIL = { TR: 1, EN: 2 };
export const DILLER = [DIL.TR, DIL.EN];

/**
 * onay_metni.hedef: metni kimin onayladığı. 3'ü API ile oluşturulamaz (taahhütname, proje sayfasında onaylanır, #216); 4 de
 * (talep metni, #721/#722: talep penceresinde okunup işaretlenir, işaret onay_kaydi'nda değil talebin kendisinde durur).
 */
export const HEDEF = { KULLANICI: 1, KISI: 2, TAAHHUTNAME: 3, TALEP: 4 };
/** Yeni metin oluştururken seçilebilenler. */
export const OLUSTURULABILIR_HEDEFLER = [HEDEF.KULLANICI, HEDEF.KISI];

/** Backend'in anahtar deseni: küçük harfle başlar, harf / rakam / alt çizgi, en çok 50. */
export const ANAHTAR_DESENI = /^[a-z][a-z0-9_]{0,49}$/;
/** Denetleyicinin kendi yol parçaları: bu adlı bir metin hiçbir zaman erişilemez ya da başka bir yola çarpar. */
export const YASAK_ANAHTARLAR = ['pending', 'history', 'surum'];

/**
 * Hatalar iki biçimde gelir: bizim {message, result, data} zarfımız ve Spring'in varsayılanı
 * {timestamp, status, error, path}. Bulunamayan sürüm yazmada 400, taslak silmede 409'dur; durum koduna değil
 * mesaja bakılır. 403'ün mesajı yetkiyi adıyla söyler ve olduğu gibi gösterilir.
 */
function onayMetniHatasi(error, varsayilan) {
  const status = error?.response?.status ?? null;
  const data = error?.response?.data;
  let mesaj = varsayilan;
  if (status === 401) mesaj = i18n.t("services:onayMetniService.oturumGecersiz401LutfenYeniden");
  else if (data && typeof data === 'object' && data.message) mesaj = data.message;
  else if (data && typeof data === 'object' && data.error) mesaj = `${data.error}${data.status ? ` (${data.status})` : ''}`;
  else if (typeof data === 'string' && data.trim()) mesaj = data;
  else if (error?.message) mesaj = error.message;
  const err = new Error(mesaj);
  err.status = status;
  return err;
}

/** Yazma uçları 2xx'te de {result:false} dönebilir; ikisini birden bir hataya çevirir. */
function zarfiAc(res, varsayilan) {
  if (res.data && res.data.result === false) throw new Error(res.data.message || varsayilan);
  return res.data;
}

/** Bir metnin gövde dosyası: `tur` 'pdf' ya da 'gorsel' (backlog #553, backend #552). */
export const DOSYA_TURU = { PDF: 'pdf', GORSEL: 'gorsel' };

/** Sunucunun `dosyalar` dizisi (okuyucu satırlarında ve GET /surum/{id}'de) → sıralı, `url` atılmış (adres dosyaId'den kurulur). */
export function dosyaListesi(deger) {
  return (Array.isArray(deger) ? deger : [])
    .map((d) => ({
      dosyaId: Number(d?.dosyaId),
      sira: Number(d?.sira) || 0,
      tur: d?.tur === DOSYA_TURU.PDF ? DOSYA_TURU.PDF : DOSYA_TURU.GORSEL,
      orijinalAd: String(d?.orijinalAd ?? ''),
      boyut: Number(d?.boyut) || 0,
    }))
    .filter((d) => Number.isFinite(d.dosyaId) && d.dosyaId > 0)
    .sort((a, b) => a.sira - b.sira);
}

/** "1,2" (GROUP_CONCAT) ya da [1, 2] ya da null → [1, 2]. */
export function dilListesi(deger) {
  if (Array.isArray(deger)) return deger.map(Number).filter((n) => DILLER.includes(n));
  if (typeof deger !== 'string') return [];
  return deger.split(',').map((s) => Number(s.trim())).filter((n) => DILLER.includes(n));
}

/** Liste satırını ekranın okuyacağı biçime getirir: dil listeleri dizi, boş başlıklar null. */
function metinSatiri(r) {
  return {
    ...r,
    hedef: Number(r.hedef) || HEDEF.KULLANICI,
    guncelDiller: dilListesi(r.guncelDiller),
    taslakDiller: dilListesi(r.taslakDiller),
    guncelBaslikTr: (r.guncelBaslikTr || '').trim() || null,
    guncelBaslikEn: (r.guncelBaslikEn || '').trim() || null,
    taslakBaslikTr: (r.taslakBaslikTr || '').trim() || null,
    taslakBaslikEn: (r.taslakBaslikEn || '').trim() || null,
    // #625 (backend #624): isActive = yalnızca GENEL hat; fuar hatları sayaçlarla gelir (sayaç yoksa 0).
    etkinFuarSayisi: Number(r.etkinFuarSayisi) || 0,
    kapaliFuarSayisi: Number(r.kapaliFuarSayisi) || 0,
  };
}

/**
 * GET /onay-metinleri — her metin bir satır: güncel yayınlanmış sürüm (no, id, başlıklar, diller), en yeni taslak
 * (aynıları), taslak sayısı ve onay sayısı (kişi metninde guncelKisiOnaySayisi). Sırayla sortOrder.
 */
export async function listOnayMetinleri({ onlyActive = false } = {}) {
  try {
    const res = await axios.get(BASE, { params: onlyActive ? { onlyActive: true } : undefined, headers: getAuthHeader() });
    return (Array.isArray(res.data) ? res.data : []).map(metinSatiri);
  } catch (e) { throw onayMetniHatasi(e, i18n.t("services:onayMetniService.onayMetinleriYuklenemedi")); }
}

/**
 * POST /onay-metinleri — yalnızca tanım: metni ve sürümü YOK, bir sürüm yayınlanana kadar hiçbir yerde görünmez.
 * Anahtar sonradan değişmez. isRequired / sortOrder GERÇEK boolean / sayı olmalı (başkası sessizce yok sayılır).
 * @returns yeni onayMetniId
 */
export async function createOnayMetni({ onayMetniKey, isRequired, sortOrder, hedef, ekNot = false }) {
  try {
    const res = await axios.post(BASE, {
      onayMetniKey: String(onayMetniKey || '').trim(),
      isRequired: Boolean(isRequired),
      sortOrder: Number(sortOrder) || 0,
      hedef: Number(hedef) || HEDEF.KULLANICI,
      // #553 (backend #552): true = fuar notu, onay penceresinin en altında kutucukla onaylanır (yalnızca kullanıcı metni)
      ...(ekNot ? { ekNot: true } : {}),
    }, { headers: getAuthHeader() });
    return zarfiAc(res, i18n.t("services:onayMetniService.metinOlusturulamadi"))?.data ?? null;
  } catch (e) {
    if (!e.response) throw e;
    throw onayMetniHatasi(e, i18n.t("services:onayMetniService.metinOlusturulamadi"));
  }
}

/**
 * PUT /onay-metinleri/{key} — kısmi: { isRequired, sortOrder, isActive }. Pasif yapmak metni geçitten çıkarır, hiçbir şey silinmez.
 * #625 (backend #624): kullanıcı metninde `isActive` yalnızca GENEL hattın düğmesidir; bir fuarın hattı setFuarHatti ile açılıp kapanır.
 */
export async function updateOnayMetni(key, degisiklik) {
  try {
    const res = await axios.put(`${BASE}/${encodeURIComponent(key)}`, degisiklik, { headers: getAuthHeader() });
    return zarfiAc(res, i18n.t("services:onayMetniService.metinGuncellenemedi"));
  } catch (e) {
    if (!e.response) throw e;
    throw onayMetniHatasi(e, i18n.t("services:onayMetniService.metinGuncellenemedi"));
  }
}

/**
 * GET /onay-metinleri/{key}/surum — sürümler, yenisi önce. yayinTarihi null = TASLAK (tek taslak / yayın göstergesi).
 * diller dizi olur. Bilinmeyen anahtar 404 değil [] döner.
 * Tarihler ISO (saat dilimli): new Date ile okunur.
 */
export async function listSurumler(key) {
  try {
    const res = await axios.get(`${BASE}/${encodeURIComponent(key)}/surum`, { headers: getAuthHeader() });
    return (Array.isArray(res.data) ? res.data : []).map((s) => ({ ...s, diller: dilListesi(s.diller), taslak: !s.yayinTarihi }));
  } catch (e) { throw onayMetniHatasi(e, i18n.t("services:onayMetniService.surumlerYuklenemedi")); }
}

/** GET /{key}/fuar satırı → ekranın okuduğu biçim: isActive gerçek boolean (satır yoksa açık), kayıtsız sayılar sayı. */
function fuarHatti(r) {
  return {
    fuarId: Number(r.fuarId),
    fuarAdiTr: (r.fuarAdiTr || '').trim() || null,
    fuarAdiEn: (r.fuarAdiEn || '').trim() || null,
    isActive: r.isActive !== false && Number(r.isActive) !== 0,
    guncelleyen: r.guncelleyen ?? null,
    updateDate: r.updateDate ?? null,
    guncelSurumNo: r.guncelSurumNo ?? null,   // null = fuarın kendi yayınlanmış sürümü yok (açıksa genel sürümü okur)
    taslakSayisi: Number(r.taslakSayisi) || 0,
  };
}

/**
 * GET /onay-metinleri/{key}/fuar — kullanıcı metninin FUAR HATLARI (#625, backend #624): kendi sürümü olan ya da düğmesi yazılmış
 * her fuar, yeni fuar önce. Personel okur. Bilinmeyen anahtar ve kişi / taahhütname metni [] döner.
 */
export async function getFuarHatlari(key) {
  try {
    const res = await axios.get(`${BASE}/${encodeURIComponent(key)}/fuar`, { headers: getAuthHeader() });
    return (Array.isArray(res.data) ? res.data : []).map(fuarHatti).filter((h) => Number.isFinite(h.fuarId) && h.fuarId > 0);
  } catch (e) { throw onayMetniHatasi(e, i18n.t("services:onayMetniService.fuarHatlariYuklenemedi")); }
}

/**
 * PUT /onay-metinleri/{key}/fuar/{fuarId} { isActive } — bir fuarın hattını açar / kapatır (`onay_metni.manage`). Kapalı hat = o fuarda
 * metin hiç sorulmaz (ne fuarın kendi sürümü ne genel sürüm); kendi sürümü olmayan bir fuar da kapatılabilir (genel sürümden çıkma).
 * Hiçbir sürüm ve onay silinmez: yeniden açınca aynı sürüm onaylarıyla döner. Kullanıcı metni ve var olan fuar; yoksa 400.
 */
export async function setFuarHatti(key, fuarId, isActive) {
  try {
    const res = await axios.put(`${BASE}/${encodeURIComponent(key)}/fuar/${fuarId}`, { isActive: !!isActive }, { headers: getAuthHeader() });
    return zarfiAc(res, i18n.t("services:onayMetniService.fuarHattiGuncellenemedi"));
  } catch (e) {
    if (!e.response) throw e;
    throw onayMetniHatasi(e, i18n.t("services:onayMetniService.fuarHattiGuncellenemedi"));
  }
}

/**
 * GET /onay-metinleri/surum/{surumId} — bir sürümün her dildeki metni. Dönen: { 1: {baslik, icerik, dosyalar}, 2: {...} };
 * metni olmayan dil anahtarsızdır. Bilinmeyen id 404 değil [] döner. İsg denetimi gibi metinler 130 KB'a varır: yalnızca
 * düzenleyici açılırken çağrılır.
 */
export async function getSurumMetinleri(surumId) {
  try {
    const res = await axios.get(`${BASE}/surum/${surumId}`, { headers: getAuthHeader() });
    const out = {};
    for (const m of Array.isArray(res.data) ? res.data : []) {
      out[m.languageId] = { baslik: m.baslik || '', icerik: m.icerik || '', dosyalar: dosyaListesi(m.dosyalar) };
    }
    return out;
  } catch (e) { throw onayMetniHatasi(e, i18n.t("services:onayMetniService.surumMetniYuklenemedi")); }
}

/**
 * Düzenleyicinin { 1: {baslik, icerik, dosyalar}, 2: {...} } durumundan istek gövdesine: yalnızca GÖVDESİ OLAN dilleri gönderir
 * (HTML'i ya da dosyası olan; boş gövde 400'dür ve bir dili taslaktan çıkarmanın yolu yoktur; sunucu eksik dili eksik sayar).
 * Yalnızca dosyası olan bir dilin HTML'i boş gider (#552: bir dosya dili boş icerik ile kaydedilebilir). Başlık kırpılır.
 */
export function metinlerGovdesi(metinler) {
  return {
    texts: DILLER
      .filter((d) => String(metinler?.[d]?.icerik ?? '').trim() !== '' || (metinler?.[d]?.dosyalar?.length ?? 0) > 0)
      .map((d) => ({ languageId: d, baslik: String(metinler[d].baslik ?? '').trim() || null, icerik: metinler[d].icerik ?? '' })),
  };
}

/**
 * POST /onay-metinleri/{key}/surum — yeni TASLAK (surumNo = en büyük + 1), kimseye görünmez. En az bir dilin gövdesi gerekir.
 * #553 (backend #552): `fuarId` o fuara özel sürüm açar (yalnızca kullanıcı metinleri; oluşunca değişmez), `dosyaKaynakSurumId`
 * aynı metnin o sürümünün dosyalarıyla başlatır (bayt paylaşılır; o zaman `metinler` boş olabilir).
 * UYARI: ortada reddedilen bir istek yarım taslak bırakabilir (sunucu geri almaz); hata sonrası sürümler yeniden okunur.
 * @returns yeni surumId
 */
export async function createSurum(key, metinler, { fuarId, dosyaKaynakSurumId } = {}) {
  try {
    const govde = { ...metinlerGovdesi(metinler) };
    if (fuarId) govde.fuarId = Number(fuarId);
    if (dosyaKaynakSurumId) govde.dosyaKaynakSurumId = Number(dosyaKaynakSurumId);
    const res = await axios.post(`${BASE}/${encodeURIComponent(key)}/surum`, govde, { headers: getAuthHeader() });
    return zarfiAc(res, i18n.t("services:onayMetniService.taslakOlusturulamadi"))?.data ?? null;
  } catch (e) {
    if (!e.response) throw e;
    throw onayMetniHatasi(e, i18n.t("services:onayMetniService.taslakOlusturulamadi"));
  }
}

/**
 * PUT /onay-metinleri/surum/{surumId} — taslağın gövdelerini kaydeder (gönderilen dil yenisiyle değişir, gönderilmeyen
 * olduğu gibi kalır). YAYINLANMIŞ sürümde 409. Aynı yarım yazma uyarısı createSurum'daki gibi geçerli.
 */
export async function updateSurum(surumId, metinler) {
  try {
    const res = await axios.put(`${BASE}/surum/${surumId}`, metinlerGovdesi(metinler), { headers: getAuthHeader() });
    return zarfiAc(res, i18n.t("services:onayMetniService.taslakKaydedilemedi"));
  } catch (e) {
    if (!e.response) throw e;
    throw onayMetniHatasi(e, i18n.t("services:onayMetniService.taslakKaydedilemedi"));
  }
}

/** POST /onay-metinleri/surum/{surumId}/publish — GERİ ALINAMAZ: sürüm donar ve herkes metni yeniden onaylar. */
export async function publishSurum(surumId) {
  try {
    const res = await axios.post(`${BASE}/surum/${surumId}/publish`, null, { headers: getAuthHeader() });
    return zarfiAc(res, i18n.t("services:onayMetniService.surumYayinlanamadi"));
  } catch (e) {
    if (!e.response) throw e;
    throw onayMetniHatasi(e, i18n.t("services:onayMetniService.surumYayinlanamadi"));
  }
}

/** DELETE /onay-metinleri/surum/{surumId} — yalnızca taslak; yayınlanmışı silmeyi sunucu reddeder (409, bulunamadı da 409). */
export async function deleteSurum(surumId) {
  try {
    const res = await axios.delete(`${BASE}/surum/${surumId}`, { headers: getAuthHeader() });
    return zarfiAc(res, i18n.t("services:onayMetniService.taslakSilinemedi"));
  } catch (e) {
    if (!e.response) throw e;
    throw onayMetniHatasi(e, i18n.t("services:onayMetniService.taslakSilinemedi"));
  }
}

/**
 * GET /onay-metinleri/{key} — bir metnin GÜNCEL yayınlanmış sürümü (ya da surumNo ile adı verilen sürüm), istenen dilde. O dilde
 * gövde yoksa Türkçesi gelir; dönen `languageId` hangisinin geldiğini söyler. Her oturum okur, ama yayınlanmamış bir sürümü
 * (taslak) yalnızca personel okur: başkasına 404 döner.
 * @returns { onayMetniKey, surumNo, yayinTarihi, baslik, icerik, languageId, ... }
 */
export async function getOnayMetni(key, { languageId, surumNo, fuarId } = {}) {
  try {
    const res = await axios.get(`${BASE}/${encodeURIComponent(key)}`, {
      params: { languageId: languageId || undefined, surumNo: surumNo || undefined, fuarId: fuarId || undefined },
      headers: getAuthHeader(),
    });
    return res.data;
  } catch (e) { throw onayMetniHatasi(e, i18n.t("services:onayMetniService.metinYuklenemedi")); }
}

/**
 * GET /onay-metinleri/current — Fuar Kuralları sayfasının okuması (#553, backend #552): her kullanıcı metni, `fuarId` fuarında
 * (verilmezse seçili fuarda) YÜRÜRLÜKTEKİ yayınlanmış sürümüyle, kullanıcının dilinde (EN yoksa TR; `languageId` gerçekte gelen)
 * ve her biri için `onaylandi` (çağıranın bu sürümü onaylayıp onaylamadığı). Fuar notu (`ekNot`) listenin SONUNDA gelir. Personel de
 * okur (kendi `onaylandi`'sı anlamsızdır; sayfa onu zaten onaylatmaz).
 * @returns [{ anahtar, baslik, icerik, dosyalar, languageId, surumNo, ekNot, onaylandi, zorunlu, sortOrder, hata }]
 */
export async function getGuncelMetinler({ languageId, fuarId } = {}) {
  try {
    const res = await axios.get(`${BASE}/current`, {
      params: { languageId: languageId || undefined, fuarId: fuarId || undefined },
      headers: getAuthHeader(),
    });
    return (Array.isArray(res.data) ? res.data : []).map(bekleyenSatiri).filter(gorunenMetin);
  } catch (e) { throw onayMetniHatasi(e, i18n.t("services:onayMetniService.guncelMetinlerYuklenemedi")); }
}

/**
 * #667 (Deniz, 2026-11-02): Fuar Kuralları penceresinde ilk iki bölüm (`giris` = Entry Rules, `fuar_kayit` = Build-up Registration Rules)
 * hiçbir fuarda GÖSTERİLMEZ. Süzme okuma anında yapılır (/current ve /pending): sayfa da geçit de bu iki metni hiç görmez, dolayısıyla
 * geçit onları beklemez ve kullanıcıya onaylatılmaz. Metinler personel sayfalarında (Onay Metinleri) durur; sunucu hâlâ /pending'de listeler.
 * WAS: hepsi gösteriliyordu.
 */
export const GIZLENEN_METIN_ANAHTARLARI = ['giris', 'fuar_kayit'];
const gorunenMetin = (m) => !GIZLENEN_METIN_ANAHTARLARI.includes(m.anahtar);

/** GET /{key} ve /pending'in tek metin biçimi → ekranın okuduğu satır. Başlık kırpılır (fuar_kayit'in TR başlığında sondaki boşluk var). */
function metinSatiriOku(m, anahtar = m?.onayMetniKey) {
  return {
    anahtar,
    baslik: String(m?.baslik ?? '').trim() || anahtar,
    icerik: m?.icerik || '',
    languageId: Number(m?.languageId) || null,
    surumNo: m?.surumNo ?? null,
    // #553 (backend #552): gövde = icerik (HTML, boş olabilir) + sırayla dosyalar; fuar notu (ekNot) listenin sonunda gelir;
    // /current her metne `onaylandi` ekler (diğer okumalarda yok).
    dosyalar: dosyaListesi(m?.dosyalar),
    ekNot: m?.ekNot === true,
    fuarId: m?.fuarId ?? null,
    onaylandi: m?.onaylandi === true,
    hata: null,
  };
}

/** /pending ve /current satırı: tek metin biçimi + zorunlu olup olmadığı ve sırası. */
function bekleyenSatiri(m) {
  return {
    ...metinSatiriOku(m),
    // Yalnızca AÇIKÇA false ise isteğe bağlı: bozuk bir yanıt metni zorunlu sayar (geçit kapalı kalır).
    zorunlu: m?.isRequired !== false,
    sortOrder: Number(m?.sortOrder) || 0,
  };
}

/** Süren /pending okumaları: anahtar = token + dil + fuar. Biten okuma silinir, sonuç önbelleğe alınmaz. */
const okumalar = new Map();

/**
 * GET /onay-metinleri/pending — GEÇİT (#540): oturumdaki kullanıcının henüz onaylamadığı metinler, sortOrder sırasıyla; boş dizi =
 * temiz. Her metin kullanıcının dilinde (EN yoksa TR; dönen `languageId` gerçekte gelen). Yalnızca hedef 1 (FDS kullanıcısı)
 * metinleri döner: taahhütname ve kişi metinleri hiç gelmez. Yanıt altı metnin tam gövdesi (~140 KB): girişte ve sayfa
 * yenilenince bir kez okunur, rota değişiminde ya da aralıklarla DEĞİL. `isRequired` false olan da gelir; geçit onu atlanabilir
 * sunar. #553 (backend #552): `fuarId` o fuarın kendi sürümlerini getirir (yoksa global sürüm); fuar notu (`ekNot`) listenin sonunda.
 * @returns [{ anahtar, baslik, icerik, languageId, surumNo, zorunlu, sortOrder, hata }]
 */
export function getBekleyenOnaylar({ languageId, fuarId, taze = false } = {}) {
  // Aynı oturum + dil için AYNI ANDA süren okuma paylaşılır: portal bağlamı açılışta iki kez yüklenir (fuarsız, sonra fuarlı) ve
  // geçit her seferinde okumak istemez; 140 KB iki kez inmesin. `taze` (onaydan sonraki okuma) hiçbir zaman eskisine katılmaz:
  // onaydan ÖNCE başlamış bir yanıt onaylananları hâlâ bekliyor gösterirdi.
  const anahtar = `${localStorage.getItem('token')}|${languageId || ''}|${fuarId || ''}`;
  if (!taze && okumalar.has(anahtar)) return okumalar.get(anahtar);
  const okuma = bekleyenOnaylariOku(languageId, fuarId);
  if (!taze) {
    okumalar.set(anahtar, okuma);
    const birak = () => { if (okumalar.get(anahtar) === okuma) okumalar.delete(anahtar); };
    okuma.then(birak, birak);
  }
  return okuma;
}

async function bekleyenOnaylariOku(languageId, fuarId) {
  try {
    const res = await axios.get(`${BASE}/pending`, {
      params: { languageId: languageId || undefined, fuarId: fuarId || undefined },
      headers: getAuthHeader(),
    });
    return (Array.isArray(res.data) ? res.data : []).map(bekleyenSatiri).filter(gorunenMetin);
  } catch (e) { throw onayMetniHatasi(e, i18n.t("services:onayMetniService.bekleyenOnaylarYuklenemedi")); }
}

/**
 * POST /onay-metinleri/{key}/accept — oturumdaki kullanıcının metnin YAYINLANMIŞ güncel sürümünü onayladığını kaydeder. Gövde yok:
 * kullanıcı, sürüm ve IP sunucudan gelir (istemci sürüm adı veremez). Aynı kullanıcı + sürüm için tekrar etkisizdir ve başarıdır.
 * `languageId` kayda, kullanıcıya gösterilen dil olarak yazılır (EN yoksa sunucu TR yazar). Hata (taslak-only metin 400, kişi /
 * taahhütname metni 400, oturum 401) mesajıyla fırlar; mesajlar istek dilindedir.
 */
export async function onayKaydet(key, { languageId, fuarId } = {}) {
  try {
    const res = await axios.post(`${BASE}/${encodeURIComponent(key)}/accept`, null, {
      params: { languageId: languageId || undefined, fuarId: fuarId || undefined },
      headers: getAuthHeader(),
    });
    return zarfiAc(res, i18n.t("services:onayMetniService.onayKaydedilemedi"));
  } catch (e) {
    if (!e.response) throw e;
    throw onayMetniHatasi(e, i18n.t("services:onayMetniService.onayKaydedilemedi"));
  }
}

/**
 * GET /onay-metinleri/history — oturumdaki kullanıcının kendi onayları, yeniden eskiye, eski sürümler dahil. `guncelMi` 1 / 0 SAYI
 * (boolean değil): 1 = hâlâ güncel metni onaylamış. Boş dizi = hiçbir şey onaylanmamış.
 */
export async function getOnayGecmisi() {
  try {
    const res = await axios.get(`${BASE}/history`, { headers: getAuthHeader() });
    return Array.isArray(res.data) ? res.data : [];
  } catch (e) { throw onayMetniHatasi(e, i18n.t("services:onayMetniService.onayGecmisiYuklenemedi")); }
}

/** /history satırlarından, GÜNCEL sürümü onaylanmış anahtarlar. */
export function onayliAnahtarlar(gecmis) {
  return new Set((gecmis || []).filter((r) => Number(r?.guncelMi) === 1).map((r) => r.onayMetniKey));
}

// ===================== GÖVDE DOSYALARI (#553, backend #552) =====================

/** Yüklenebilir uzantılar (sunucu da bakar: Dosya Ayarları › Onay metni dosyaları; ilk baytlar uzantıya uymalı). */
export const DOSYA_UZANTILARI = ['.pdf', '.jpg', '.jpeg', '.png'];

/** Dosya hataları: gövde ArrayBuffer ya da JSON olabilir; sunucunun mesajı varsa o, yoksa durum koduna göre sabit metin. */
function dosyaHatasi(error, varsayilan) {
  const status = error?.response?.status ?? null;
  let mesaj = varsayilan;
  let data = error?.response?.data;
  if (Object.prototype.toString.call(data) === '[object ArrayBuffer]') {   // instanceof değil: jsdom / iframe gibi başka bir dünyadan gelebilir
    try { data = JSON.parse(new TextDecoder().decode(data)); } catch { data = null; }
  }
  if (status === 401) mesaj = i18n.t("services:onayMetniService.oturumGecersiz401LutfenYeniden");
  else if (data && typeof data === 'object' && data.message) mesaj = data.message;
  else if (status === 404) mesaj = i18n.t("services:onayMetniService.dosyaBulunamadi");
  const err = new Error(mesaj);
  err.status = status;
  return err;
}

/**
 * GET /onay-metinleri/dosya/{dosyaId} — gövde dosyası (PDF ya da görsel), oturum başlıklarıyla BLOB olarak okunur: <img>/<iframe>
 * başlık gönderemez. Yayınlanmış sürümün dosyasını her giriş, taslağınkini yalnızca personel okur (başkasına 404).
 * @returns { veri: ArrayBuffer, tip: content-type }
 */
export async function getDosya(dosyaId) {
  try {
    const res = await axios.get(`${BASE}/dosya/${dosyaId}`, { responseType: 'arraybuffer', headers: getAuthHeader() });
    return { veri: res.data, tip: String(res.headers?.['content-type'] ?? '') };
  } catch (e) { throw dosyaHatasi(e, i18n.t("services:onayMetniService.dosyaYuklenemedi")); }
}

/**
 * POST /onay-metinleri/surum/{surumId}/dosya (multipart: languageId, file) — TASLAĞIN o dilinin sonuna dosya ekler. Yayınlanmış
 * sürümde 409, taahhütname metninde 400 (gövdesi HTML kalır). Boyut ve uzantı sunucuda denetlenir.
 * @returns eklenen dosya (normalleştirilmiş)
 */
export async function uploadDosya(surumId, languageId, file) {
  const form = new FormData();
  form.append('languageId', String(languageId));
  form.append('file', file);
  try {
    const res = await axios.post(`${BASE}/surum/${surumId}/dosya`, form, { headers: getAuthHeader() });
    const out = zarfiAc(res, i18n.t("services:onayMetniService.dosyaEklenemedi"));
    return dosyaListesi([out?.data])[0] ?? null;
  } catch (e) {
    if (!e.response) throw e;
    throw dosyaHatasi(e, i18n.t("services:onayMetniService.dosyaEklenemedi"));
  }
}

/** DELETE /onay-metinleri/surum/{surumId}/dosya/{dosyaId} — yalnızca taslak (yayınlanmışta 409). */
export async function deleteDosya(surumId, dosyaId) {
  try {
    const res = await axios.delete(`${BASE}/surum/${surumId}/dosya/${dosyaId}`, { headers: getAuthHeader() });
    return zarfiAc(res, i18n.t("services:onayMetniService.dosyaSilinemedi"));
  } catch (e) {
    if (!e.response) throw e;
    throw dosyaHatasi(e, i18n.t("services:onayMetniService.dosyaSilinemedi"));
  }
}

/**
 * PUT /onay-metinleri/surum/{surumId}/dosya/sira { languageId, dosyaIds } — taslağın o dilinin dosyalarını verilen sıraya koyar
 * (dosyaIds dilin HER dosyasını bir kez içermeli). @returns dilin dosyaları yeni sırayla
 */
export async function siralaDosyalar(surumId, languageId, dosyaIds) {
  try {
    const res = await axios.put(`${BASE}/surum/${surumId}/dosya/sira`, { languageId, dosyaIds }, { headers: getAuthHeader() });
    return dosyaListesi(zarfiAc(res, i18n.t("services:onayMetniService.dosyaSirasiKaydedilemedi"))?.data);
  } catch (e) {
    if (!e.response) throw e;
    throw dosyaHatasi(e, i18n.t("services:onayMetniService.dosyaSirasiKaydedilemedi"));
  }
}
